import React from 'react';
import { Row, Col } from 'react-bootstrap';
import TalentCard from './TalentCard';
import CircularProgress from './CircularProgress';
import { TalentType, PlayerTalent } from '../types/talents';
import { useAppTranslation } from '../i18n/useAppTranslation';

interface TalentListProps {
  talents: PlayerTalent[];
  loading?: boolean;
  onUpgradeClick?: (talentId: TalentType) => void;
  onViewClick?: (talentId: TalentType) => void;
  canUpgradeTalent?: (talentId: TalentType) => boolean;
  upgradingTalent?: TalentType | null;
  compact?: boolean;
  showUpgradeButtons?: boolean;
  className?: string;
  emptyMessage?: string;
  emptyIcon?: string;
  columnsPerRow?: number;
}

const TalentList: React.FC<TalentListProps> = ({
  talents,
  loading = false,
  onUpgradeClick,
  onViewClick,
  canUpgradeTalent,
  upgradingTalent,
  compact = false,
  showUpgradeButtons = true,
  className = '',
  emptyMessage = "No talents found",
  emptyIcon = "star",
  columnsPerRow = 3
}) => {
  const { t } = useAppTranslation("research");
  if (loading) {
    return (
      <div className="text-center py-5">
        <CircularProgress />
        <div className="mt-2">{t("research:talentsUi.loadingTalents")}</div>
      </div>
    );
  }

  if (!talents || talents.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className={`fas fa-${emptyIcon} fa-3x mb-3 opacity-50`}></i>
        <h5>{t("research:talentsUi.noTalents")}</h5>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Calculate responsive columns
  const getColSize = () => {
    switch (columnsPerRow) {
      case 1: return { xs: 12 };
      case 2: return { xs: 12, sm: 6 };
      case 3: return { xs: 12, sm: 6, lg: 4 };
      case 4: return { xs: 12, sm: 6, md: 4, lg: 3 };
      case 6: return { xs: 12, sm: 6, md: 4, lg: 2 };
      default: return { xs: 12, sm: 6, lg: 4 };
    }
  };

  const colProps = getColSize();

  return (
    <div className={`talent-list ${className}`}>
      <Row className="g-3">
        {talents.map((talent) => (
          <Col key={talent.talentId} {...colProps}>
            <TalentCard
              talent={talent}
              onUpgradeClick={onUpgradeClick}
              onViewClick={onViewClick}
              canUpgrade={canUpgradeTalent ? canUpgradeTalent(talent.talentId) : false}
              isUpgrading={upgradingTalent === talent.talentId}
              compact={compact}
              showUpgradeButton={showUpgradeButtons}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default TalentList;
